var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PublicIcon from '@mui/icons-material/Public';
import { useAuth, getCompanyKey, getLoginMethod, resolveAutoLoginMethod } from '@react-components/auth';
import { brand } from './brand';
import { MicrosoftLogo } from './icons';
import { resolveApiBase } from './resolveApiBase';
import { AuthScaffold, PrimaryButton, SecondaryButton } from './AuthScaffold';
/**
 * Decide which provider buttons to show from a company's enabledLoginButtons.
 * When the setting is absent, only ArcGIS is shown (the platform default).
 * @param enabled - enabledLoginButtons from the company settings, if any.
 */
function getVisibleProviders(enabled) {
    var _a, _b, _c;
    var showAgol = enabled === undefined || enabled.includes('agol');
    var showSso = (_a = enabled === null || enabled === void 0 ? void 0 : enabled.includes('sso')) !== null && _a !== void 0 ? _a : false;
    var showMicrosoft = (_b = enabled === null || enabled === void 0 ? void 0 : enabled.includes('microsoft')) !== null && _b !== void 0 ? _b : false;
    var showOtp = (_c = enabled === null || enabled === void 0 ? void 0 : enabled.includes('otp')) !== null && _c !== void 0 ? _c : false;
    return {
        showAgol: showAgol,
        showSso: showSso,
        showMicrosoft: showMicrosoft,
        showOtp: showOtp,
        hasSecondary: showSso || showMicrosoft || showOtp
    };
}
/**
 * Sync URL params into the auth context and handle the Microsoft/SAML
 * callbacks. Mirrors the original package AuthPage effects; the dependency
 * arrays are intentionally narrow (run on mount / when the key inputs change),
 * so `auth` and `searchParams` are deliberately omitted.
 * @param auth - the auth context from useAuth().
 * @param searchParams - the current URLSearchParams.
 * @param companyKey - the :companyKey route param.
 * @param appId - the :appId route param (auto-launch app / portal detection).
 */
function useAuthPageEffects(auth, searchParams, companyKey, appId) {
    // Sync companyKey + URL params into auth context (mirrors the RCM AuthPage
    // parseUrlParams: companyKey, appId → autoLaunchAppId, login-redirect).
    // setAreUrlParamsParsed(true) is required for isUsingPortal() to engage
    // the ArcGIS Enterprise portal (/sharing) auth URL.
    // auto-sign-in is NOT armed here — the auto-fire effect below resolves
    // WHICH provider to dispatch (from login-method / stored loginMethod /
    // company settings) before calling setIsAutoLogin.
    useEffect(function () {
        if (companyKey) {
            auth.setCompanyKey(companyKey);
        }
        if (appId) {
            auth.setAutoLaunchAppId(appId);
        }
        var loginRedirect = searchParams.get('login-redirect');
        if (loginRedirect) {
            auth.setLoginRedirectUrl(loginRedirect);
        }
        auth.setAreUrlParamsParsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyKey, appId]);
    // Handle Microsoft OAuth callback (?auth=microsoft)
    useEffect(function () {
        if (searchParams.get('auth') !== 'microsoft') {
            return;
        }
        var loginRedirect = searchParams.get('login-redirect');
        auth.completeMicrosoftLogin().then(function (success) {
            if (success && loginRedirect) {
                window.location.replace(loginRedirect);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Handle SAML auto-trigger (?auth=saml) once SSO status resolves
    useEffect(function () {
        if (searchParams.get('auth') === 'saml' && auth.isSsoEnabled) {
            auth.loginWithSSO();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.isSsoEnabled]);
}
/**
 * Load a company's enabled login buttons. The "default" company (and the empty
 * key) skip the request and fall back to the platform default (ArcGIS only).
 * @param companyKey - the :companyKey route param.
 */
function useCompanyLoginButtons(companyKey) {
    var _a = useState(undefined), enabledLoginButtons = _a[0], setEnabledLoginButtons = _a[1];
    var _b = useState(false), settingsLoaded = _b[0], setSettingsLoaded = _b[1];
    useEffect(function () {
        if (!companyKey || companyKey === 'default') {
            setSettingsLoaded(true);
            return;
        }
        var apiBase = resolveApiBase();
        fetch("".concat(apiBase, "/hermes/common/loginLogoutSettings/").concat(encodeURIComponent(companyKey)))
            .then(function (res) { return (res.ok ? res.json() : null); })
            .then(function (json) {
            var _a;
            if ((_a = json === null || json === void 0 ? void 0 : json.data) === null || _a === void 0 ? void 0 : _a.enabledLoginButtons) {
                setEnabledLoginButtons(json.data.enabledLoginButtons);
            }
        })
            .catch(function () { return undefined; })
            .finally(function () { return setSettingsLoaded(true); });
    }, [companyKey]);
    return { enabledLoginButtons: enabledLoginButtons, settingsLoaded: settingsLoaded };
}
/**
 * Resolve and dispatch the auto-sign-in provider once company settings load.
 *
 * Resolution (first valid candidate): the login-method query param → the
 * stored loginMethod (how the user last signed in) → the company's single
 * enabled provider. A candidate is valid when settings are absent or it is in
 * enabledLoginButtons. When the settings are absent and nothing resolves, the
 * ArcGIS flow fires — the pre-login-method behavior of bare auto-sign-in
 * URLs. When settings list several providers and nothing resolves, no
 * provider fires and the chooser renders.
 *
 * 'otp' cannot be auto-completed (needs an emailed code) — the user is landed
 * on the OTP screen instead of a provider redirect.
 * @param auth - the auth context from useAuth().
 * @param searchParams - the current URLSearchParams.
 * @param companyKey - the resolved company key (route param or storage).
 * @param settingsLoaded - whether the enabledLoginButtons fetch settled.
 * @param enabledLoginButtons - the company's enabled providers, if any.
 * @param otpPath - base path of the OTP page (e.g. "/sso2/otp").
 */
function useAutoSignIn(auth, searchParams, companyKey, settingsLoaded, enabledLoginButtons, otpPath) {
    var navigate = useNavigate();
    var _a = useState(searchParams.get('auto-sign-in') === 'true' ? 'pending' : 'idle'), autoFireState = _a[0], setAutoFireState = _a[1];
    useEffect(function () {
        var _a;
        if (autoFireState !== 'pending' || !settingsLoaded) {
            return;
        }
        var resolved = (_a = resolveAutoLoginMethod(searchParams.get('login-method'), getLoginMethod(), enabledLoginButtons)) !== null && _a !== void 0 ? _a : (enabledLoginButtons === undefined ? 'agol' : undefined);
        if (resolved === 'otp') {
            if (companyKey) {
                setAutoFireState('dispatched');
                navigate("".concat(otpPath, "/").concat(companyKey));
            }
            else {
                setAutoFireState('chooser');
            }
            return;
        }
        if (resolved) {
            // The provider AuthProvider dispatches the actual login once its
            // mount-time existing-login check completes.
            auth.setAutoLoginMethod(resolved);
            auth.setIsAutoLogin(true);
            setAutoFireState('dispatched');
            return;
        }
        setAutoFireState('chooser');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFireState, settingsLoaded, enabledLoginButtons]);
    return autoFireState;
}
/**
 * The stack of provider sign-in buttons, driven by the visible-provider flags.
 * @param props - the visible-provider flags and per-provider click handlers.
 */
function ProviderButtons(props) {
    var showAgol = props.showAgol, showSso = props.showSso, showMicrosoft = props.showMicrosoft, showOtp = props.showOtp, hasSecondary = props.hasSecondary, onAgol = props.onAgol, onMicrosoft = props.onMicrosoft, onSso = props.onSso, onOtp = props.onOtp;
    return (_jsxs(Stack, { spacing: 1.75, children: [showAgol && (_jsx(PrimaryButton, { onClick: onAgol, startIcon: _jsx(PublicIcon, { sx: { fontSize: '1.1rem' } }), children: 'Sign In' })), showAgol && hasSecondary && (_jsx(Divider, { sx: {
                    '&::before, &::after': { borderColor: brand.divider }
                }, children: _jsx(Typography, { component: "span", sx: {
                        fontFamily: brand.mono,
                        fontSize: '0.62rem',
                        letterSpacing: '0.22em',
                        color: brand.textMuted
                    }, children: 'OR CONTINUE WITH' }) })), showMicrosoft && (_jsx(SecondaryButton, { onClick: onMicrosoft, startIcon: _jsx(MicrosoftLogo, {}), children: 'Sign in with Microsoft' })), showSso && (_jsx(SecondaryButton, { onClick: onSso, startIcon: _jsx(LockOutlinedIcon, { sx: { fontSize: '1.05rem' } }), children: 'SSO' })), showOtp && (_jsx(SecondaryButton, { onClick: onOtp, startIcon: _jsx(MailOutlineIcon, { sx: { fontSize: '1.05rem' } }), children: 'Sign in with a code' }))] }));
}
/**
 * AuthPage — the sign-in landing screen.
 *
 * Reads :companyKey from the URL, fetches the company's enabledLoginButtons
 * setting, and renders the appropriate provider buttons. All login behavior
 * comes from useAuth() in @react-components/auth — this component is purely
 * the sso2 presentation of that flow.
 * @param root0
 * @param root0.basePath
 */
export function AuthPage(_a) {
    var _b = _a.basePath, basePath = _b === void 0 ? '' : _b;
    var _c = useParams(), _d = _c.companyKey, companyKeyParam = _d === void 0 ? '' : _d, appId = _c.appId;
    // Storage fallback: expiry redirects from older app bundles use the bare
    // /sso2 URL with no :companyKey — the geoSso blob still remembers the key
    // (token expiry does not clear it), so company settings stay resolvable.
    var companyKey = companyKeyParam || getCompanyKey();
    var searchParams = useSearchParams()[0];
    var navigate = useNavigate();
    var auth = useAuth();
    var prefix = basePath ? "/".concat(basePath) : '';
    useAuthPageEffects(auth, searchParams, companyKey, appId);
    var _e = useCompanyLoginButtons(companyKey), enabledLoginButtons = _e.enabledLoginButtons, settingsLoaded = _e.settingsLoaded;
    var autoFireState = useAutoSignIn(auth, searchParams, companyKey, settingsLoaded, enabledLoginButtons, "".concat(prefix, "/otp"));
    // Keep the spinner up through the auto-fire window ('pending' while the
    // settings resolve, 'dispatched' until the provider redirect navigates
    // away) so the chooser never flashes before an auto-login.
    if (!settingsLoaded ||
        auth.isBusy ||
        autoFireState === 'pending' ||
        autoFireState === 'dispatched') {
        return (_jsx(Box, { sx: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                bgcolor: brand.bg
            }, children: _jsx(CircularProgress, { sx: { color: brand.primary } }) }));
    }
    var providers = getVisibleProviders(enabledLoginButtons);
    return (_jsx(AuthScaffold, { children: _jsx(ProviderButtons, __assign({}, providers, { onAgol: function () { return auth.login(); }, onMicrosoft: function () { return auth.loginWithMicrosoft(); }, onSso: function () { return auth.loginWithSSO(); }, onOtp: function () { return navigate("".concat(prefix, "/otp/").concat(companyKey)); } })) }));
}
export default AuthPage;
